class EstimateDeliveryDate extends HTMLElement {
    static DEBUG = false;

    // Business days added to "today" per destination
    static DELIVERY_DAYS = { domestic: 2, eu: 4, northAmerica: 5, world: 6 };

    // Country codes grouped by delivery window (anything not listed falls back to `world`)
    static COUNTRY_GROUPS = {
        domestic: ['FR', 'MC'],
        eu: ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB'],
        northAmerica: ['US', 'CA']
    };

    // Extra day added on top of the window while a sales period is active
    static SALES_EXTRA_DAYS = 1;

    static WEEKDAY = { sunday: 0, saturday: 6 };

    static GEO_IP = {
        url: 'https://ipapi.co/json/',
        cacheKey: 'visitor_country_cache',
        cacheTTL: 4 * 60 * 60 * 1000 // 4 hours
    };

    connectedCallback () {
        this.salesMode = this.dataset.salesMode === 'true';
        this.shopCountry = this.dataset.shopCountry || null;
        this.geoCountry = null;
        this._render();
    }

    _log (message, data = null) {
        if (!EstimateDeliveryDate.DEBUG) return;
        console.log(`[EstimateDeliveryDate] ${message}`, data || '');
    }

    // Resolve the destination country. The market chosen in the footer is mirrored by
    // Shopify into `localization.country.iso_code` (passed as data-shop-country), so it
    // takes priority. Geo IP is only a fallback for the rare case where that's empty.
    _resolveCountry () {
        if (this.shopCountry) {
            this._log('Using market country:', this.shopCountry);
            return this.shopCountry;
        }
        if (this.geoCountry) return this.geoCountry;

        const cache = this._readGeoCache();
        if (cache) {
            if (Date.now() - cache.timestamp > EstimateDeliveryDate.GEO_IP.cacheTTL) {
                this._fetchGeo();
            }
            this.geoCountry = cache.country;
            return this.geoCountry;
        }

        this._fetchGeo();
        return null;
    }

    _readGeoCache () {
        const raw = window.localStorage.getItem(EstimateDeliveryDate.GEO_IP.cacheKey);
        if (!raw) return null;
        try {
            const cache = JSON.parse(raw);
            return cache && cache.country ? cache : null;
        } catch (e) {
            this._log('Error parsing Geo IP cache, refetching.', e);
            return null;
        }
    }

    _fetchGeo () {
        this._log('Fetching Geo IP...');
        fetch(EstimateDeliveryDate.GEO_IP.url)
            .then(res => res.json())
            .then(data => {
                if (!data || !data.country) return;
                this.geoCountry = data.country;
                window.localStorage.setItem(
                    EstimateDeliveryDate.GEO_IP.cacheKey,
                    JSON.stringify({ country: data.country, timestamp: Date.now() })
                );
                this._log('Geo IP fetched and cached:', this.geoCountry);
                this._render();
            })
            .catch(error => {
                this._log('Geo IP fetch failed, falling back to shop country.', error);
                this.geoCountry = this.shopCountry;
                this._render();
            });
    }

    _addedDays (countryCode) {
        const { DELIVERY_DAYS, COUNTRY_GROUPS, SALES_EXTRA_DAYS } = EstimateDeliveryDate;
        let addDays = DELIVERY_DAYS.world;
        if (COUNTRY_GROUPS.domestic.includes(countryCode)) {
            addDays = DELIVERY_DAYS.domestic;
        } else if (COUNTRY_GROUPS.eu.includes(countryCode)) {
            addDays = DELIVERY_DAYS.eu;
        } else if (COUNTRY_GROUPS.northAmerica.includes(countryCode)) {
            addDays = DELIVERY_DAYS.northAmerica;
        }
        if (this.salesMode) addDays += SALES_EXTRA_DAYS;
        return addDays;
    }

    // Add the delivery window as business days: weekends don't count toward the window,
    // so a window spanning a Sat+Sun lands two days later. This also guarantees the
    // estimate never falls on a weekend.
    _estimateDate (countryCode) {
        const { saturday, sunday } = EstimateDeliveryDate.WEEKDAY;
        const date = new Date();
        let remaining = this._addedDays(countryCode);
        while (remaining > 0) {
            date.setDate(date.getDate() + 1);
            const day = date.getDay();
            if (day !== saturday && day !== sunday) remaining -= 1;
        }
        return date;
    }

    _formatDate (date) {
        const locale = window.Shopify?.locale || 'en';
        return date
            .toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' })
            .replace(/^./, str => str.toUpperCase());
    }

    _render () {
        const span = this.querySelector('span');
        if (!span) return;

        const country = this._resolveCountry();
        if (!country) return; // unknown yet — Geo IP fetch will re-render

        try {
            span.innerText = this._formatDate(this._estimateDate(country));
            this.classList.remove('shimmer');
        } catch (err) {
            console.error('[EstimateDeliveryDate] Error rendering estimate', err);
        }
    }
}

if (!window.customElements.get('estimate-delivery-date')) {
    window.customElements.define('estimate-delivery-date', EstimateDeliveryDate);
}
